import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, ErrorComponent } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'

import { membershipSchema } from '@/api/mem/memberMembership'
import { useUpdateMembershipMutation } from '@/api/mem/memberMembershipQueryOptions'
import { mainMembershipQueryOptions } from '@/api/membershipQueryOptions'
import { MembershipType } from '@/api/memberships'
import { NoPermissionError } from '@/api/NoPermissionError'
import MembershipForm from '@/components/form/MembershipForm'
import FormDebug from '@/components/FormDebug'
import { NoPermissionErrorComponent } from '@/components/NoPermissionErrorComponent'
import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/membershipSettings',
)({
    component: MembershipComponent,
    errorComponent: ({ error }) => {
        if (error instanceof NoPermissionError) {
            return (
                <NoPermissionErrorComponent error={error}></NoPermissionErrorComponent>
            )
        }
        return <ErrorComponent error={error}></ErrorComponent>
    },
})

function MembershipComponent() {
    const params = Route.useParams()
    const membershipQuery = useSuspenseQuery(
        mainMembershipQueryOptions(params.memberUuid),
    )
    const membership = membershipQuery.data

    const updateMembershipMutation = useUpdateMembershipMutation(
        membership.member.uuid,
        () => { },
        () => { },
    )

    const {
        control,
        register,
        handleSubmit,
        watch,
        formState: { isDirty, isValid, errors },
    } = useForm<MembershipType>({
        resolver: yupResolver<MembershipType, any, any>(membershipSchema),
        mode: 'onBlur',
        shouldFocusError: true,
    })

    const onSubmit = (data: MembershipType) => {
        if (isValid || Object.keys(errors).length === 0) {
            updateMembershipMutation.mutate({ ...membership, ...data })
            enqueueSnackbar(t('action.changed', { entity: t('entity.membership') }), {
                preventDuplicate: true,
                variant: 'success',
            })
        } else {
            console.log(errors)
        }
    }

    const onError = (error: any) => {
        console.log('error ' + JSON.stringify(error))
    }

    useEffect(() => {
        const subscription = watch((_value, { name }) => {
            if (name) {
                handleSubmit(onSubmit, onError)()
            }
        })

        return () => subscription.unsubscribe()
    })

    const writeable = membership.permissions
        ? membership.permissions.some((perm) => perm === 'write')
        : false

    if (membershipQuery.isLoading || membershipQuery.isFetching) {
        return <LoadProgress />
    }

    return (
        <>
            <form>
                <MembershipForm
                    control={control}
                    register={register}
                    membership={membership}
                    writeable={writeable}
                />
            </form>
            <FormDebug
                errors={errors}
                dirty={isDirty}
                valid={isValid}
                value={membership}
            />
            {import.meta.env.VITE_DEBUG === 'true' && <DevTool control={control} />}
        </>
    )
}
